import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { SERVICES } from "@/content/services";
import { Picture } from "@/components/media/Picture";
import { siteConfig } from "@/config/site";


export const Route = createFileRoute("/services/")({
  head: () => ({ ...pageMeta({
    path: "/services/",
    title: `SEO Services in ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state} — 12 Growth Disciplines`,
    description:
      `Twelve integrated SEO services from an ${siteConfig.contact.address.city} agency — technical, local, content, links, AEO/GEO, analytics, CRO, PPC — run as one growth engine.`,


  }), scripts: ldScripts(breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services/" }])) }),
  component: ServicesIndex,
});

const FLAGSHIP = {
  slug: "seo",
  category: "Search · Flagship",
  title: "SEO",
  tagline:
    "The integrated SEO programme — technical, content, authority, local, and AEO/GEO run on our proven SEO growth process.",
};

function ServicesIndex() {
  const byCategory = SERVICES.reduce(
    (acc, s) => {
      (acc[s.category] ||= []).push(s);
      return acc;
    },
    {} as Record<string, typeof SERVICES>,
  );

  const categoryOrder = ["Search", "AI Search", "Local", "Growth", "Measurement", "Paid", "Foundation", "Vertical"];
  const sortedCategories = Object.keys(byCategory).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b),
  );

  return (
    <div>
      <section className="container-page pt-20 md:pt-28 pb-10">
          <EyebrowLabel>{siteConfig.contact.address.city} SEO services · 12 disciplines</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[1.02] max-w-4xl text-balance">
            Twelve SEO disciplines.
            <span className="text-gradient"> One {siteConfig.contact.address.city} growth engine.</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            {siteConfig.name} doesn't sell one-size-fits-all SEO packages. We run integrated
            search programs for {siteConfig.contact.address.city}, {siteConfig.contact.address.state} and Central Texas businesses — including Round Rock, Cedar Park, Pflugerville, Georgetown
            and Leander — pulling from twelve tightly-coupled disciplines that

            make organic search, local pack rankings, Google Business Profile visibility
            and AI Overview citations compound together. Every engagement uses the same
            South {siteConfig.contact.address.city} team, the same reporting stack (Semrush, Search Console, GA4,
            Looker Studio) and the same standard of proof: named clients, dated windows,
            reproducible dashboards.

          </p>
          <Picture
            variantKey="austin"
            alt=""
            width={1600}
            height={720}
            priority
            className="mt-10 rounded-2xl overflow-hidden border border-border block"
            imgClassName="w-full h-auto object-cover"
            sizes="(min-width: 1024px) 1100px, 100vw"
          />
        </section>

        {/* Flagship spotlight */}
        <section className="container-page py-10">
          <Link
            to="/services/seo/"
            className="group block surface-card overflow-hidden grid lg:grid-cols-[1.15fr_1fr]"
          >
            <div className="p-8 md:p-12">
              <EyebrowLabel>{FLAGSHIP.category}</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-5xl leading-tight">
                {FLAGSHIP.title}
              </h2>
              <p className="mt-4 text-muted-foreground text-lg">{FLAGSHIP.tagline}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-primary font-medium">
                Read the flagship page
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </div>
            </div>
            <div className="p-8 md:p-12 bg-surface-2 flex flex-col justify-center gap-6">
              {["Technical SEO", "On-Page + Content", "Authority (Links + PR)", "Local + GBP", "AEO / GEO"].map((p, i) => (
                <div key={p} className="flex items-baseline gap-4">
                  <div className="font-display text-xl text-primary shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-sm">{p}</div>
                </div>
              ))}
            </div>
          </Link>
        </section>

        {/* All services by category */}
        {sortedCategories.map((category) => (
          <section key={category} className="container-page py-10">
            <div className="flex items-baseline justify-between gap-6 border-b border-border pb-4">
              <h2 className="font-display text-2xl md:text-3xl">{category}</h2>
              <div className="text-[0.7rem] uppercase tracking-widest text-muted-foreground">
                {byCategory[category].length} discipline
                {byCategory[category].length > 1 ? "s" : ""}
              </div>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {byCategory[category].map((s) => (
                <Link
                  key={s.slug}
                  to="/services/$slug/"
                  params={{ slug: s.slug }}
                  className="group surface-card p-6 md:p-7 flex flex-col hover:-translate-y-0.5 transition-transform"
                >
                  <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                    {s.eyebrow}
                  </div>
                  <h3 className="mt-2 font-display text-2xl">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground text-sm leading-relaxed line-clamp-3">
                    {s.hero.deck}
                  </p>
                  <ul className="mt-5 grid grid-cols-2 gap-1.5">
                    {s.tldr.slice(0, 4).map((t) => (
                      <li
                        key={t}
                        className="text-[0.7rem] text-muted-foreground flex items-start gap-2"
                      >
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />
                        <span className="line-clamp-1">{t}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 inline-flex items-center gap-1 text-primary text-sm">
                    Explore {s.title.toLowerCase()}
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

        {/* CTA */}
        <section className="container-page py-16">
          <div className="surface-card p-8 md:p-12 grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <EyebrowLabel>Not sure where to start?</EyebrowLabel>
              <h2 className="mt-3 font-display text-3xl md:text-4xl">
                We'll tell you what to run first.
              </h2>
              <p className="mt-3 text-muted-foreground max-w-xl">
                Book a free strategy call. We'll audit your current footprint
                and recommend the two or three disciplines that will move the
                needle fastest — even if it isn't us running them.
              </p>
            </div>
            <Link
              to="/contact/"
              search={{ location: "", service: "" }}
              className="inline-flex whitespace-nowrap rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
            >
              Book a strategy call →
            </Link>
          </div>
        </section>
    </div>
  );
}
